import React, { useCallback, useEffect, useRef } from 'react';
import styles from './BotaoDropdown.module.css';
import Icon from '@/components/Icon';
import { mdiChevronDown, mdiChevronUp } from '@mdi/js';
import { BotaoVariants } from './Botao';
import btnStyles from './Botao.module.css';
import HBox from './layout/HBox';
import ReactDOM from 'react-dom';

type DropdownPosition =
  | 'top-right'
  | 'top-left'
  | 'bottom-right'
  | 'bottom-left';

type BotaoDropdownProps = {
  variant?: BotaoVariants;
  value?: string;
  onChange?: (value: string) => void;
  placeholder?: string;
  type?: string;
  required?: boolean;
  options?: { id: string; label: React.ReactNode }[];
  disabled?: boolean;
  children: ({ closeDropdown }: { closeDropdown: () => void }) => React.ReactNode;
  text?: React.ReactNode;
  icon?: string;
  noArrow?: boolean;
  onKeyDown?: (e: React.KeyboardEvent) => void;
  position?: DropdownPosition | 'auto';
  dropdownStyle?: React.CSSProperties;
  [key: string]: any;
};

export default function BotaoDropdown({
  variant = 'primary',
  value,
  onChange,
  placeholder,
  type = 'text',
  required,
  options,
  disabled,
  children,
  text,
  icon,
  noArrow,
  onKeyDown,
  position = 'auto',
  dropdownStyle,
  ...props
}: Readonly<BotaoDropdownProps>) {
  const [open, setOpen] = React.useState<{
    isOpen: boolean;
    position: DropdownPosition;
  }>({ isOpen: false, position: 'bottom-right' });
  const [ready, setReady] = React.useState(false);

  const handleContainerClick = (e: any) => {
    e.stopPropagation();

    if (disabled) return;

    toggleDropdown();
  };

  const toggleDropdown = useCallback(() => {
    console.log('toggle', open.isOpen);
    setReady(false);
    setOpen(prevState => ({ ...prevState, isOpen: !prevState.isOpen }));
  }, [open]);

  const openDropdown = () => {
    setReady(false);
    setOpen(prevState => ({ ...prevState, isOpen: true }));
  };

  const closeDropdown = () => {
    setReady(false);
    setOpen(prevState => ({ ...prevState, isOpen: false }));
  };

  const optionsContainerRef = useRef<HTMLDivElement>(null);
  const containerRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    if (optionsContainerRef.current) {
      console.log('position', position);
      if (position === 'auto') {
        const rect = optionsContainerRef.current.getBoundingClientRect();
        const availableSpaceBelow = window.innerHeight - rect.bottom;
        const availableSpaceAbove = rect.top;
        const shouldOpenAbove =
          availableSpaceBelow < rect.height &&
          availableSpaceAbove > availableSpaceBelow;

        const availableSpaceRight = window.innerWidth - rect.right;
        const availableSpaceLeft = rect.left;
        const shouldOpenLeft =
          availableSpaceRight > rect.width &&
          availableSpaceLeft < availableSpaceRight;

        console.log('shouldOpenAbove', availableSpaceAbove, rect.height, availableSpaceBelow);
        console.log('shouldOpenLeft', availableSpaceLeft, rect.width, availableSpaceRight);

        const _position = shouldOpenAbove
          ? shouldOpenLeft
            ? 'top-left'
            : 'top-right'
          : shouldOpenLeft
            ? 'bottom-left'
            : 'bottom-right';

        console.log('position', _position);
        setOpen(prevState => ({ ...prevState, position: _position }));
      } else {
        setOpen(prevState => ({
          ...prevState,
          position: position as DropdownPosition,
        }));
      }
    }
    setReady(true);
  }, [open.isOpen, position, optionsContainerRef.current]);

  useEffect(() => {
    const handleDropdownClose = (e: MouseEvent) => {
      // [data-open][data-side]: popups flutuantes da BaseUI (Combobox etc.), que
      // são portados para o <body> — fora de .options. Clicar neles (opção do
      // multi-seletor, busca) não deve fechar este dropdown.
      if ((e.target instanceof HTMLElement || e.target instanceof SVGElement) &&
        (e.target.closest(`.${styles.options}`) || e.target.closest('[data-open][data-side]'))) {
        return;
      }
      closeDropdown();
    }

    document.addEventListener('click', handleDropdownClose);
    return () => document.removeEventListener('click', handleDropdownClose);
  }, [])

  const [offset, setOffset] = React.useState({
    top: 0,
    bottom: 0,
    left: 0,
    right: 0,
    width: '100%',
  });

  useEffect(() => {
    const adjust = () => {
      if (open.isOpen) {
        const rect = containerRef.current!.getBoundingClientRect();
        const optRect = optionsContainerRef.current!.getBoundingClientRect();

        console.log(rect);
        console.log(
          window.innerHeight,
          optRect.height,
          rect.bottom + rect.height + window.scrollY,
        );

        setOffset({
          top: rect.bottom + window.scrollY,
          bottom: Math.min(
            window.innerHeight - optRect.height + window.scrollY,
            window.innerHeight - rect.bottom + rect.height + window.scrollY,
          ),
          left: rect.left + window.scrollX + 12,
          right: Math.min(
            window.innerWidth - rect.right + window.scrollX,
            window.innerWidth - rect.left + window.scrollX - 12,
          ),
          width: rect.width + 'px',
        });
      }
    };
    window.addEventListener('scroll', adjust);
    window.addEventListener('resize', adjust);
    adjust();

    return () => {
      window.removeEventListener('scroll', adjust);
      window.removeEventListener('resize', adjust);
    };
  }, [open]);

  return (
    <div>
      <div
        ref={containerRef}
        className={[
          btnStyles.button,
          styles.container,
          btnStyles[variant],
          disabled ? styles.disabled : null,
        ].join(' ')}
        tabIndex={disabled ? undefined : 0}
        onKeyDown={onKeyDown}
        onClick={handleContainerClick}
      >
        <HBox>
          {icon && <Icon path={icon} size={1.5} />}
          {!!text && <div>{text}</div>}
        </HBox>
      </div>
      {open.isOpen && containerRef.current &&
        ReactDOM.createPortal(
          <div
            ref={optionsContainerRef}
            className={[
              styles.options,
            ].join(' ')}
            style={{
              top: open.position == 'bottom-left' || open.position == 'bottom-right' ? offset.top : undefined,
              bottom: open.position == 'bottom-left' || open.position == 'bottom-right' ? undefined : offset.bottom,
              left: open.position === 'bottom-left' || open.position === 'top-left' ? offset.left : undefined,
              right: open.position === 'bottom-right' || open.position === 'top-right' ? offset.right : undefined,
              width: offset.width,
              opacity: ready ? 1 : 0,
              ...dropdownStyle,
            }}
          >
            {children?.({ closeDropdown })}
          </div>,
          document.querySelector('#root')!,
        )}
    </div>
  );
}
